import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
    id: "export-dialog-title",
    "data-testid": "export-dialog-title",
    class: "text-xl font-semibold"
};
const _hoisted_2 = { class: "grid grid-cols-1 gap-4 sm:grid-cols-2" };
const _hoisted_3 = {
    key: 0,
    id: "date-range-error",
    "data-testid": "validation-error",
    class: "text-sm text-red-600 dark:text-red-400",
    role: "alert",
    "aria-live": "polite"
};
const _hoisted_4 = { class: "flex justify-end gap-3 mt-2" };
import { computed, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { Modal, Button, SelectInput, DatePickerInput } from "@eudonet/opal";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ExportDialog',
    props: {
        open: { type: Boolean },
        loading: { type: Boolean }
    },
    emits: ["close", "submit"],
    setup(__props, { emit: __emit }) {
        const { t, locale } = useI18n();
        const props = __props;
        const emit = __emit;
        // Form data — date values are in display format from DatePickerInput
        const type = ref("payment");
        const startDate = ref("");
        const endDate = ref("");
        // Snapshot the display format active when each date was picked, so a later
        // locale change does not corrupt parsing at submit time.
        const startDateFormat = ref("");
        const endDateFormat = ref("");
        // Report type options for SelectInput
        const reportTypeOptions = computed(() => [
            { value: "payment", label: t("dashboard.exportDialog.types.payment") },
            { value: "transfer", label: t("dashboard.exportDialog.types.transfer") },
        ]);
        // Locale-aware date format for Flowbite datepicker
        const dateFormat = computed(() => {
            switch (locale.value) {
                case "fr-FR":
                case "fr-CA":
                case "fr":
                    return "dd/mm/yyyy";
                case "nl-NL":
                case "nl":
                    return "dd-mm-yyyy";
                default:
                    return "yyyy-mm-dd";
            }
        });
        const dateLanguage = computed(() => {
            if (locale.value.startsWith("fr"))
                return "fr";
            if (locale.value.startsWith("nl"))
                return "nl";
            return "en";
        });
        const datePlaceholder = computed(() => {
            switch (locale.value) {
                case "fr-FR":
                case "fr-CA":
                case "fr":
                    return "JJ/MM/AAAA";
                case "nl-NL":
                case "nl":
                    return "DD-MM-JJJJ";
                default:
                    return "YYYY-MM-DD";
            }
        });
        function displayToIso(displayDate, fmt) {
            if (!displayDate)
                return "";
            if (fmt === "dd/mm/yyyy") {
                const [day, month, year] = displayDate.split("/");
                return `${year}-${month}-${day}`;
            }
            if (fmt === "dd-mm-yyyy") {
                const [day, month, year] = displayDate.split("-");
                return `${year}-${month}-${day}`;
            }
            return displayDate;
        }
        // Capture the format active at pick time to avoid a locale-change race at submit.
        watch(startDate, (v) => {
            if (v)
                startDateFormat.value = dateFormat.value;
        });
        watch(endDate, (v) => {
            if (v)
                endDateFormat.value = dateFormat.value;
        });
        const isDateRangeValid = computed(() => {
            if (!startDate.value || !endDate.value)
                return false;
            const isoStart = displayToIso(startDate.value, startDateFormat.value || dateFormat.value);
            const isoEnd = displayToIso(endDate.value, endDateFormat.value || dateFormat.value);
            return new Date(isoStart) <= new Date(isoEnd);
        });
        function emitClose() {
            emit("close");
        }
        function handleSubmit() {
            if (!isDateRangeValid.value)
                return;
            emit("submit", {
                type: type.value,
                startDate: displayToIso(startDate.value, startDateFormat.value || dateFormat.value),
                endDate: displayToIso(endDate.value, endDateFormat.value || dateFormat.value),
            });
        }
        watchEffect(() => {
            if (!props.open) {
                type.value = "payment";
                startDate.value = "";
                endDate.value = "";
                startDateFormat.value = "";
                endDateFormat.value = "";
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                "model-value": __props.open,
                size: "sm",
                "show-footer": false,
                dismissable: true,
                "aria-labelledby": "export-dialog-title",
                onClose: emitClose
            }, {
                header: _withCtx(() => [
                    _createElementVNode("h2", _hoisted_1, _toDisplayString(_ctx.$t("dashboard.exportDialog.title")), 1 /* TEXT */)
                ]),
                default: _withCtx(() => [
                    _createElementVNode("form", {
                        "data-testid": "export-form",
                        class: "flex flex-col gap-4",
                        onSubmit: _withModifiers(handleSubmit, ["prevent"])
                    }, [
                        _createVNode(_unref(SelectInput), {
                            modelValue: type.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((type).value = $event)),
                            "data-testid": "report-type-select",
                            label: _ctx.$t('dashboard.exportDialog.typeLabel'),
                            options: reportTypeOptions.value,
                            required: "",
                            "full-width": ""
                        }, null, 8 /* PROPS */, ["modelValue", "label", "options"]),
                        _createElementVNode("div", _hoisted_2, [
                            _createVNode(_unref(DatePickerInput), {
                                id: "startDate",
                                modelValue: startDate.value,
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((startDate).value = $event)),
                                "test-id": "start-date-input",
                                label: _ctx.$t('dashboard.exportDialog.startDate'),
                                format: dateFormat.value,
                                language: dateLanguage.value,
                                placeholder: datePlaceholder.value,
                                buttons: false,
                                required: "",
                                "full-width": ""
                            }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "placeholder"]),
                            _createVNode(_unref(DatePickerInput), {
                                id: "endDate",
                                modelValue: endDate.value,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((endDate).value = $event)),
                                "test-id": "end-date-input",
                                label: _ctx.$t('dashboard.exportDialog.endDate'),
                                format: dateFormat.value,
                                language: dateLanguage.value,
                                placeholder: datePlaceholder.value,
                                buttons: false,
                                required: "",
                                "full-width": ""
                            }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "placeholder"])
                        ]),
                        (startDate.value && endDate.value && !isDateRangeValid.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.$t("dashboard.exportDialog.validation.startBeforeEnd")), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true),
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(_unref(Button), {
                                variant: "secondary",
                                "data-testid": "close-button",
                                label: _ctx.$t('dashboard.exportDialog.close'),
                                onClick: emitClose
                            }, null, 8 /* PROPS */, ["label"]),
                            _createVNode(_unref(Button), {
                                variant: "primary",
                                type: "submit",
                                "data-testid": "generate-button",
                                label: _ctx.$t('dashboard.exportDialog.generate'),
                                loading: __props.loading,
                                disabled: !isDateRangeValid.value
                            }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                        ])
                    ], 32 /* NEED_HYDRATION */)
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["model-value"]));
        };
    }
});
